import { FieldProps } from "@/core/components/Field";
import { Box } from "@mui/system";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { useFlightResults } from "../../../hooks/useFlightResults/useFlightResults";
import { CALCULATE_DELAY } from "@/core/types/types";
import { getCalculatedDelay } from "@/utils";
import { isISOTimeString } from "@/core/utils";
import { Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { parseISO } from "date-fns";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import DateTimeField from "@/core/components/DateTimeField/DateTimeField";

const FlightDelayDateTimeField = ({ field }: FieldProps) => {
  const { key, defaultValue } = field;
  const [delay, setDelay] = useState<string>("");
  const initialValue: string | undefined = isISOTimeString(defaultValue || "")
    ? defaultValue
    : undefined;
  const dateTimeInitialValue = useRef<string | undefined>(initialValue);
  const { watch, setValue } = useFormContext();
  const dateTimeFieldValue = watch(key);
  const translations = {
    delay: t("common.datetime.delay"),
    noDelay: t("common.datetime.noDelay"),
    calculationError: t("common.datetime.calculationError"),
    singleDayTranslation: t("common.datetime.day"),
    pluralDayTranslation: t("common.datetime.days"),
    singleHourTranslation: t("common.datetime.hour"),
    pluralHourTranslation: t("common.datetime.hours"),
    singleMinuteTranslation: t("common.datetime.minute"),
    pluralMinuteTranslation: t("common.datetime.minutes"),
  };
  /**
   * FR-2731 AC defines, that value is either an ISO date of type string,
   * or variableName of type string
   */
  const comparisonDate: string | undefined =
    field.properties?.[CALCULATE_DELAY];

  const isISODate: boolean = isISOTimeString(comparisonDate ?? "");
  const variablesToRequest: string | null =
    !isISODate && typeof comparisonDate !== "undefined" ? comparisonDate : null;

  const { result }: any = useGetPreviousAnswers(variablesToRequest);
  const comparisonDateValue: string = isISODate
    ? comparisonDate
    : result[comparisonDate!!];

  const { selectedFlightDate, setSelectedFlightDate } = useFlightResults({
    departureDate: comparisonDateValue,
  });

  useEffect(() => {
    if (initialValue) {
      const valueIsDateTime = initialValue.includes("T");
      const updatedFlightDate = valueIsDateTime
        ? initialValue.split("T")[0]
        : initialValue;
      setSelectedFlightDate(updatedFlightDate ?? null);
    }
  }, [initialValue]);

  useEffect(() => {
    if (dateTimeFieldValue && selectedFlightDate) {
      const currentDateTimeString: string = `${selectedFlightDate}T${dateTimeFieldValue.slice(
        11
      )}`;
      // Remove ms by default for more  precise comparison of minutes
      const formattedDate: string = `${comparisonDateValue.slice(0, 17)}00`;
      const dateToCompareWith: Date = parseISO(formattedDate);
      const currentDate: Date = parseISO(currentDateTimeString);
      const delay = getCalculatedDelay(
        currentDate,
        dateToCompareWith,
        translations
      );

      dateTimeInitialValue.current = dateTimeFieldValue;
      setDelay(delay);
      setValue(field.key, currentDateTimeString);
    } else {
      setDelay("");
    }
  }, [dateTimeFieldValue, selectedFlightDate]);

  return (
    <>
      <DateTimeField
        field={field}
        initialValue={dateTimeInitialValue.current ?? selectedFlightDate}
        onChange={setSelectedFlightDate}
      />
      <Box mt={2} p={0}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {translations.delay}: {delay || "--"}
        </Typography>
      </Box>
    </>
  );
};

export default FlightDelayDateTimeField;
